import React, { useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { Box, Button } from "@material-ui/core";
import { useStyles } from "./styles";
import { mkConfig, generateCsv, download } from "export-to-csv";
import DownloadIcon from "@mui/icons-material/Download";

interface Props {
  columns: any;
  rows?: any;
  height?: number;
  loader?: boolean;
  defaultPerPageRecord?: number;
  deletedRow?: boolean;
  hideFooter?: boolean;
  isExport?: boolean;
  isFooter?: boolean;
  exportHandler?: any;
  filename?: any;
  isEditMode?: boolean;
  onClickEdit?: () => void;
  onClickUpdate?: () => void;
  onClickCancel?: () => void;
  topToolbarRightActions?: React.ReactNode;
  enableExpanding?: boolean;
  renderDetailPanel?: (row: any) => React.ReactNode;
  getRowCanExpand?: (row: any) => boolean;
}

const CommonMaterialGrid = ({
  columns,
  rows,
  height,
  loader = false,
  defaultPerPageRecord = 10,
  deletedRow = false,
  hideFooter = true,
  isExport = false,
  isFooter = false,
  exportHandler,
  filename,
  isEditMode = false,
  onClickEdit,
  onClickUpdate,
  onClickCancel,
  topToolbarRightActions,
  enableExpanding = false,
  renderDetailPanel,
  getRowCanExpand,
}: Props) => {
  const classes = useStyles();
  const emptyMessage = useMemo(() => {
    return <div className={classes.label}>No results found</div>;
  }, []);

  const muiTableBodyCellProps = (row: any) => {
    return {
      sx:
        (deletedRow && row.row.original.isDeleted) || row.row.original?.paymentStatus == "Void"
          ? { color: "red" }
          : {},
    };
  };

  const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
    filename: filename || "default",
  });

  const handleExportData = async (data: any) => {
    const row = await data.map((row: any) => row.original);
    const records = await exportHandler(row);

    if (records) {
      const csv = generateCsv(csvConfig)(records);
      download(csvConfig)(csv);
    }
  };

  return (
    <div className={classes.root}>
      <Box style={{ width: "100%", height: "100%" }}>
        <MaterialReactTable
          enableStickyFooter={isFooter}
          columns={columns}
          data={rows}
          enableExpanding={enableExpanding}
          enableColumnFilterModes
          enableColumnOrdering={true}
          enableGrouping
          enablePinning
          enableRowActions={false}
          enableColumnResizing
          enableRowSelection={false}
          initialState={{
            density: "compact",
            pagination: { pageSize: defaultPerPageRecord, pageIndex: 0 },
          }}
          enableStickyHeader
          enableFullScreenToggle={false}
          enablePagination={hideFooter}
          // initialState={{ showColumnFilters: true }}
          state={{ isLoading: loader }}
          muiTableContainerProps={{
            sx: { height: height },
          }}
          muiExpandButtonProps={({ row }) => {
            const canExpand = row.getCanExpand();
            return {
              disabled: !canExpand,
              sx: !canExpand ? { visibility: "hidden" } : undefined,
            };
          }}
          renderEmptyRowsFallback={() => emptyMessage}
          muiTableBodyCellProps={muiTableBodyCellProps}
          muiTableHeadCellProps={{
            sx: () => ({
              background: "black",
              // borderRight: "white",
              color: "white",
            }),
          }}
          renderDetailPanel={renderDetailPanel}
          getRowCanExpand={getRowCanExpand}
          renderTopToolbarCustomActions={({ table }) => (
            <Box
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div>
                {isExport && !isEditMode ? (
                  <Button
                    disabled={rows.length == 0}
                    onClick={() => handleExportData(table.getPrePaginationRowModel().rows)}
                    startIcon={<DownloadIcon />}
                  >
                    Export data to Excel
                  </Button>
                ) : null}
              </div>
              <div>
                {topToolbarRightActions ? <>{topToolbarRightActions}</> : null}
                {!isEditMode && onClickEdit ? (
                  <Button
                    style={{ backgroundColor: "#72CA00", color: "#ffffff" }}
                    variant="contained"
                    onClick={onClickEdit}
                  >
                    Edit
                  </Button>
                ) : null}
                {isEditMode ? (
                  <>
                    {onClickCancel ? (
                      <Button
                        style={{
                          marginRight: 8,
                          color: "#333",
                          borderColor: "#e0e0e0",
                          backgroundColor: "#e0e0e0",
                        }}
                        variant="outlined"
                        onClick={onClickCancel}
                      >
                        Reset
                      </Button>
                    ) : null}
                    {onClickUpdate ? (
                      <Button
                        color="primary"
                        variant="contained"
                        style={{ backgroundColor: "#72CA00" }}
                        onClick={onClickUpdate}
                      >
                        Update
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </div>
            </Box>
          )}
        />
      </Box>
    </div>
  );
};

export default CommonMaterialGrid;
